/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as React from 'react';
import { useContext, useState, useCallback, useMemo, useRef } from 'react';

import { Utils } from '@/utils/utils.ts';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@/components/Loader.tsx';
import { Tag } from 'primereact/tag';
import { OverlayPanel } from 'primereact/overlaypanel';
import { TabPanel, TabView } from 'primereact/tabview';
import { ProjectDescription } from '@/components/ProjectDescription.tsx';
import { processesContext } from 'ogc-processes-components';
import { useTranslation } from 'react-i18next';
import { Message } from 'primereact/message';
import { AntflowApiEndpoint } from '@/services/dataprovider';
import { AppRoutes } from '@/constants/routes.ts';

import { ProjectProcesses } from '@/components/ProcessesWrapper/ProjectProcesses.tsx';
import { processCommander, useQueryFilters } from '@/hooks/useQueryFilters.tsx';
import { AxiosError } from 'axios';
import { useProjectPath } from '@/hooks/useProjectPath';
import { GitReferenceSelector } from '@/components/GitReferenceSelector';
import { Button } from 'primereact/button';
import { PublishProjectDialog } from '@/components/ProjectComponents/PublishProjectDialog';
import { UnPublishProjectDialog } from '@/components/ProjectComponents/UnPublishProjectDialog copy';
import { ButtonGroup } from 'primereact/buttongroup';
interface Props {
  className?: string;
  setMeta?: (meta: { path: string; link: string }) => void;
}

export const ProjectDetailPage: React.FC<Props> = ({ setMeta }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { projectPath, toBrowserProjectPath, pathname } = useProjectPath();

  const { processesEndpoint } = useContext(processesContext)!;

  const { queryParams } = useQueryFilters({ commander: processCommander });

  const [openPublishDialog, setOpenPublishDialog] = useState<boolean>(false);

  const [openUnpublishDialog, setOpenUnpublishDialog] = useState<boolean>(false);

  const [branch, setBranch] = useState<string>();

  const publicGroupsOverlay = useRef<OverlayPanel>(null);

  const queryTabs = queryParams.get('tabs');

  const [tab, setTab] = useState(queryTabs ? parseInt(queryTabs) : 0);

  const {
    data: project,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ['get_project_info', projectPath],
    queryFn: async () => {
      if (projectPath) {
        const endpoint = processesEndpoint as AntflowApiEndpoint;
        const data = await endpoint.get_project_detail(projectPath.replace('/processings/projects', '/projects'));
        const hrefLink = data.links.find((link) => link.rel === 'project')?.href;
        if (hrefLink) {
          setMeta?.({ path: data.path, link: hrefLink });
        }
        if (data) {
          setBranch(data.ref);
        }
        return data;
      }
    },
    refetchOnWindowFocus: false,
    throwOnError() {
      return false;
    },
    retry(_, error: AxiosError) {
      if (error.status === 404) {
        if (error.response?.data) {
          const errorData = error.response.data as { detail: { instance: string; traceback: string } };
          const { instance, traceback } = errorData.detail;
          if (instance && traceback) {
            const newPath = pathname.replace(instance, traceback);
            navigate(newPath, { replace: true });
            return false;
          }
        }
      }
      return false;
    }
  });

  const updateProjectBranch = useCallback(
    (branch: string) => {
      setBranch(branch);
      if (project) {
        const default_branchLink = project.branches[project.default_branch];
        const template = default_branchLink.replace(project.default_branch, '{{branch}}');

        const url = toBrowserProjectPath(template.replace('{{branch}}', branch));
        navigate(AppRoutes.projects.detail.url(url) || '');
      }
    },
    [navigate, project, toBrowserProjectPath]
  );

  const handleOnSelect = useCallback(
    (e: { value: string }) => {
      const { value } = e;
      if (project) {
        const link = project.branches[value] || project.tags[value];
        const url = toBrowserProjectPath(link);
        navigate(AppRoutes.projects.detail.url(url) || '');
      }
    },
    [navigate, project, toBrowserProjectPath]
  );

  const projectLink = useMemo(() => {
    const hrefLink = project?.links.find((link) => link.rel === 'project')?.href;
    if (!hrefLink) {
      return null;
    }
    return hrefLink;
  }, [project]);

  const canPublish = useMemo(() => {
    return Object.hasOwn(project || {}, 'is_public') && project?.is_public === false;
  }, [project]);

  const canUnPublish = useMemo(() => {
    return Object.hasOwn(project || {}, 'is_public') && project?.is_public === true;
  }, [project]);

  const publicGroups = useMemo(() => {
    return project?.public?.groups ?? [];
  }, [project]);

  return (
    <div className="w-full py-1 h-full">
      <div className="flex h-full">
        {isFetching ? (
          <div className="h-full w-full flex align-items-center">
            <Loader />
          </div>
        ) : project ? (
          <div className="flex flex-column align-items-start w-full mt-3">
            <div className="w-full flex align-items-center justify-content-between mb-3">
              <div className="flex flex-column align-content-start text-left">
                <div className="flex flex-row align-items-baseline">
                  <a
                    target="_blank"
                    href={`${project.url}${branch ? '/-/tree/'.concat(branch) : ''}`}
                    style={{ all: 'unset' }}
                  >
                    <i className="pi pi-external-link cursor-pointer mr-2" />
                    <span className="inline-block cursor-pointer font-bold text-xl hover:text-color-secondary hover:underline">
                      {project.name}
                    </span>
                  </a>

                  <GitReferenceSelector
                    className="font-bold ml-3"
                    onManualRefAdd={updateProjectBranch}
                    handleOnSelect={handleOnSelect}
                    reference={branch}
                    project={project}
                  />
                </div>
              </div>

              {canPublish && (
                <ButtonGroup>
                  <Button
                    size="small"
                    outlined
                    icon="pi pi-lock"
                    data-pr-classname="text-sm"
                    className="cursor-pointer border-right-none"
                    tooltip={
                      project.shared_permissions?.can_read_repository
                        ? t(['project.publish.info'])
                        : t(['project.publish.disabled'])
                    }
                    tooltipOptions={{ position: 'top', className: 'text-sm' }}
                  />
                  <Button
                    size="small"
                    data-pr-classname="text-sm"
                    label={t(['project.publish.label', 'Publish'])}
                    aria-label={t(['project.publish.label', 'Publish'])}
                    disabled={!project.shared_permissions?.can_read_repository}
                    tooltip={
                      project.shared_permissions?.can_read_repository
                        ? t(['project.publish.tooltip'])
                        : t(['project.publish.disabled'])
                    }
                    tooltipOptions={{
                      position: 'bottom',
                      showOnDisabled: !project.shared_permissions?.can_read_repository,
                      className: 'text-sm'
                    }}
                    onClick={() => {
                      setOpenPublishDialog(true);
                    }}
                  />
                  {!project.shared_permissions?.can_read_repository && (
                    <Button
                      size="small"
                      icon="pi pi-question-circle"
                      severity="secondary"
                      outlined
                      className="border-round-right"
                      tooltip={t('spaces.deploy_guide')}
                      tooltipOptions={{ position: 'top', className: 'text-sm' }}
                      onClick={() => {
                        const docs = '#/docs/references/project-setup#sharing-with-antflow-group-of-users';
                        const url = `${window.location.origin}${window.location.pathname}${docs}`;
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                    />
                  )}
                </ButtonGroup>
              )}

              {canUnPublish && (
                <ButtonGroup>
                  <Button
                    size="small"
                    outlined
                    icon={<i className="pi pi-unlock font-semibold" />}
                    className="cursor-pointer border-right-none"
                    aria-haspopup
                    onMouseEnter={(e) => publicGroupsOverlay.current?.show(e, e.currentTarget)}
                    onMouseLeave={() => publicGroupsOverlay.current?.hide()}
                    onFocus={(e) => publicGroupsOverlay.current?.show(e, e.currentTarget)}
                    onBlur={() => publicGroupsOverlay.current?.hide()}
                  />
                  <OverlayPanel ref={publicGroupsOverlay} dismissable={false}>
                    <div className="flex flex-column gap-2" style={{ maxWidth: '20rem' }}>
                      <span className="text-sm font-medium text-color-secondary">
                        <i className="pi pi-users mr-2" />
                        {t(['project.public_groups.title', 'Shared with groups'])}
                      </span>

                      {publicGroups.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {publicGroups.map((group, idx) => (
                            <Tag key={idx} value={group} />
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-color-secondary">
                          {t(['project.public_groups.everyone', 'Visible to everyone'])}
                        </span>
                      )}
                      {project.shared_permissions?.can_read_env === false && (
                        <Message
                          severity="warn"
                          icon="pi pi-exclamation-triangle"
                          className="text-sm"
                          content={t(['project.public_groups.limited_authorization', ''])}
                        />
                      )}
                    </div>
                  </OverlayPanel>

                  <Button
                    size="small"
                    data-pr-classname="text-sm"
                    label={t(['project.unpublish.label', 'Unpublish'])}
                    aria-label={t(['project.unpublish.label', 'Unpublish'])}
                    tooltip={t([
                      'project.unpublish.tooltip',
                      'Unpublish this public project to make its processes unavailable to other users.'
                    ])}
                    tooltipOptions={{ position: 'bottom' }}
                    onClick={() => {
                      setOpenUnpublishDialog(true);
                    }}
                  />
                </ButtonGroup>
              )}
            </div>

            <div className="flex align-items-center flex-wrap mt-1">
              {project.topics.map((topic, idx) => (
                <Tag className="mr-2" severity="secondary" key={idx} value={topic} />
              ))}
            </div>

            <TabView
              activeIndex={tab > 2 ? 0 : tab}
              onTabChange={(e) => {
                setTab(e.index);
                if (projectLink) {
                  navigate(
                    AppRoutes.projects.detail.url(
                      toBrowserProjectPath(projectLink) + Utils.buildWithQueryParamsUrl({ tabs: e.index.toString() })
                    )
                  );
                }
              }}
              className={'mt-2 w-full'}
            >
              <TabPanel
                headerClassName="text-sm"
                leftIcon={<i className="mr-2 pi pi-file text-lg" />}
                header={t(['misc.presentation', 'Presentation'])}
              >
                <ProjectDescription project={project} />
              </TabPanel>

              <TabPanel
                headerClassName="text-sm"
                leftIcon={<i className="mr-2 pi pi-code text-lg" />}
                header={t(['misc.processes', 'Processes'])}
              >
                <ProjectProcesses project={project} />
              </TabPanel>
            </TabView>

            <PublishProjectDialog
              visible={openPublishDialog}
              onHide={() => setOpenPublishDialog(false)}
              onSuccess={() => {
                setOpenPublishDialog(false);
                refetch();
              }}
              project={project}
            />

            <UnPublishProjectDialog
              visible={openUnpublishDialog}
              onHide={() => setOpenUnpublishDialog(false)}
              onSuccess={() => {
                setOpenUnpublishDialog(false);
                refetch();
              }}
              project={project}
            />
          </div>
        ) : (
          <div className={'flex flex-column w-full h-full '}>
            <Message
              severity="error"
              className="mt-6"
              content={
                <div className="text-left w-full p-4">
                  <span className="block font-semibold text-lg mb-4">{t(['project.error.title'])}</span>

                  <div className="mb-3">
                    <span className="block font-bold">{t(['project.error.reason_title'])}:</span>
                    <span className="block"> {t(['project.error.404.reason'])}</span>
                  </div>

                  <div className="mb-3">
                    <span className="block font-bold">{t(['project.error.error_code_title'])}:</span>
                    <span className="block">{t(['project.error.404.error_code'])}</span>
                  </div>

                  <div className="mb-3">
                    <span className="block font-bold">{t(['project.error.project_requested'])}:</span>
                    <span className="block">{pathname}</span>
                  </div>
                </div>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};
